<template>
    <div class="flex items-center justify-center w-full gap-2">
        <Select
            v-model="selection"
            :options="authorList"
            optionLabel="label"
            class="w-full"
            :loading="isLoading"
            filter
            @filter="debouncedOnTextChange"
            @value-change="onChange"
            @show="onShow"
            resetFilterOnHide
        >
            <template #value="slotProps">
                <div>
                    <div v-if="slotProps.value" class="flex items-center">
                        <div>{{ slotProps.value.label }}</div>
                    </div>
                    <span class="invisible" v-else> Hello </span>
                </div>
            </template>

            <template #option="slotProps">
                <div class="flex items-center" :id="`item-${slotProps.option.value}`">
                    <div>
                        {{ slotProps.option.label }}
                    </div>
                </div>
            </template>
        </Select>

        <Button
            v-if="authService.hasPermission(PermissionsEnum.createAuthor)"
            v-tooltip.top="'Create new author'"
            icon="pi pi-plus"
            aria-label="Save"
            size="small"
            @click="onShowDialog"
        />
    </div>

    <AuthorDialog v-if="showDialog" v-model:visible="showDialog" @authorCreated="onAuthorCreated"> </AuthorDialog>
</template>

<script setup>
import { ref, defineEmits, inject, nextTick, onMounted, defineProps, watch } from "vue";
import { Select } from "primevue";
import Button from "primevue/button";
import AuthorDialog from "@/ui/admin/authors/AuthorDialog.vue";
import { debounce } from "lodash";
import { PermissionsEnum } from "@/core/enums/PermissionsEnum";

const selection = ref(null);
const authorList = ref([]);
const showDialog = ref(false);
const isLoading = ref(false);

const props = defineProps({
    selectedAuthor: { type: Object, default: null },
});

const stop = watch(
    () => props.selectedAuthor,
    () => {
        selection.value = props.selectedAuthor;
        stop();
    },
);

let currentPage = 1;
let pages = null;
let searchTerm = "";
const authorService = inject("authorService");
const authService = inject("authService");
const emit = defineEmits(["selectedAuthor"]);

const observer = new IntersectionObserver(
    (entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                nextPage();
            }
        });
    },
    {
        threshold: 0.1,
    },
);

onMounted(async () => {
    await searchAuthors();
});

const searchAuthors = async () => {
    try {
        isLoading.value = true;
        const response = await authorService.paginatedSearch(currentPage, searchTerm);

        if (response) {
            const newList = response.data.map((author) => {
                return {
                    value: author.id,
                    label: `${author.firstName} ${author.lastName}`,
                };
            });

            authorList.value = currentPage == 1 ? newList : [...authorList.value, ...newList];

            pages = response.totalPages;
            currentPage = response.currentPage;
            isLoading.value = false;

            observer.disconnect();
            if (pages == currentPage || !pages) {
                return;
            }

            if (authorList.value.length) {
                const lastItem = authorList.value.at(-1);

                nextTick(() => {
                    const selector = `item-${lastItem.value}`;
                    const element = document.getElementById(selector);

                    if (element) {
                        observer.observe(element);
                    }
                });
            }
        }
    } finally {
    }
};

const onTextChange = async (newValue) => {
    currentPage = 1;
    searchTerm = newValue.value;

    await searchAuthors();
};

const debouncedOnTextChange = debounce(onTextChange, 500);

const nextPage = () => {
    if (isLoading.value || !pages || currentPage == pages) {
        return;
    }

    currentPage++;

    isLoading.value = true;
    searchAuthors();
};

const onAuthorCreated = (newAuthor) => {
    authorList.value = [
        {
            value: newAuthor.id,
            label: `${newAuthor.firstName} ${newAuthor.lastName}`,
        },
    ];

    nextTick(() => {
        selection.value = authorList.value[0];
        emit("selectedAuthor", selection.value);
    });
};

const onChange = (newValue) => {
    emit("selectedAuthor", newValue);
};

const onShowDialog = (event) => {
    showDialog.value = true;
};

const onShow = () => {
    const lastItem = authorList.value.at(-1);

    const selector = `item-${lastItem.value}`;
    observer.observe(document.getElementById(selector));
};
</script>

<style scoped></style>
